import React, { useState, useEffect } from 'react';
import {
  Box, Button, Grid, Alert,
  CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, Typography,
  TextField, IconButton, Chip, Checkbox, FormControlLabel, 
  Autocomplete, TextFieldProps
} from '@mui/material';
import { Add, Save, Delete as DeleteIcon, CheckBox as CheckBoxIcon, CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon } from '@mui/icons-material';
import { Checkbox as MuiCheckbox } from '@mui/material';
import DynamicTable from '../../Components/Dynamic/DynamicTable';
import DynamicOffcanvas from '../../Components/Dynamic/DynamicOffcanvas';
import DynamicField from '../../Components/Dynamic/DynamicField';
import { useUser } from '../../Context/UserContext';
import API_URLS from '../../Services/api/action';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SchemeManagement = () => {
  const { token } = useUser();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [speciesOptions, setSpeciesOptions] = useState([]);

  const [open, setOpen] = useState(false);

  // Form State
  const getInitialFormState = () => ({
    name: '',
    percentage: '',
    species_preferred: [] 
  });

  const [formData, setFormData] = useState(getInitialFormState());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const getHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // --- FETCHERS ---

  const fetchSpeciesOptions = async () => {
    try {
      const res = await fetch('/api/center/trees', { headers: getHeaders() });
      const data = await res.json();
      const options = Array.isArray(data) ? data : data?.results || data?.data || [];
      setSpeciesOptions(options);
    } catch (err) {
      console.error("Error fetching species options:", err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/${API_URLS.scheme}`, { headers: getHeaders() });
      const data = await res.json();
      const schemes = data.results || data || [];
      setTableData(schemes);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSpeciesOptions();
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (open && !editMode) {
      setFormData(getInitialFormState());
      setErrors({});
    }
  }, [open, editMode]);

  // --- HANDLERS ---

  const validate = () => {
    let newErrors = {};
    if (!formData.name) newErrors.name = "Scheme name is required";
    
    if (!formData.percentage && formData.percentage !== 0) {
      newErrors.percentage = "Percentage is required";
    } else if (Number(formData.percentage) < 0) {
      newErrors.percentage = "Percentage cannot be negative";
    } else if (Number(formData.percentage) > 100) {
      newErrors.percentage = "Percentage cannot exceed 100%";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // ✅ FIXED: Restrict percentage to 0-100 range
    if (name === 'percentage') {
      // Allow empty string (for clearing the field)
      if (value === '') {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
          setErrors(prev => ({ ...prev, [name]: '' }));
        }
        return;
      }
      
      const numValue = Number(value);
      
      // Only update if it's a valid number within 0-100 range
      // This prevents typing any value above 100
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
      // If value is > 100 or < 0, simply don't update state
      // The input field will not reflect the invalid value
      
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        percentage: Number(formData.percentage),
        species_preferred: formData.species_preferred.map(s => ({
          id: s.id,
          name: s.species_name || s.name
        }))
      };

      let url = `/api/${API_URLS.scheme}`;
      let method = 'POST';

      if (editMode && editingId) {
        url = `${url}/${editingId}`;
        method = 'PUT';
      }

      const res = await fetch(url, {
        method: method,
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Operation failed");

      setOpen(false);
      setEditMode(false);
      setEditingId(null);
      fetchData();

    } catch (err) {
      console.error("Submit error:", err);
      alert("Error saving scheme.");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit
  const handleEdit = (row) => {
    setEditMode(true);
    setEditingId(row.id);
    
    let preferred = row.species_preferred || [];
    if (typeof preferred === 'string') {
      try { preferred = JSON.parse(preferred); } catch (e) { preferred = []; }
    }

    setFormData({
      name: row.name || '',
      percentage: row.percentage || '',
      species_preferred: preferred
    });

    setOpen(true);
  };

  // Delete
  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedRow) return;

    try {
      await fetch(`/api/${API_URLS.scheme}/${selectedRow.id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      setDeleteOpen(false);
      setSelectedRow(null);
      fetchData();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // --- TABLE COLUMNS ---
  const columns = [
    { field: 'id', headerName: 'ID' },
    { 
      field: 'name', 
      headerName: 'Scheme Name',
      render: (row) => <span style={{ fontWeight: 'bold' }}>{row.name}</span>
    },
    { 
      field: 'percentage', 
      headerName: 'Percentage',
      render: (row) => <Chip label={`${row.percentage}%`} size="small" color="primary" variant="outlined" />
    },
    { 
      field: 'species_preferred', 
      headerName: 'Preferred Species',
      width: 300,
      render: (row) => {
        let species = row.species_preferred || [];
        if (typeof species === 'string') {
          try { species = JSON.parse(species); } catch (e) { species = []; }
        }
        
        if (!species.length) return <Typography variant="caption" color="textSecondary">None</Typography>;
        
        const names = species.map(s => s.name || s.species_name).join(', ');
        return (
          <Typography 
            variant="caption" 
            sx={{ 
              maxWidth: 300, 
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.5,
              py: 0.5,
            }} 
            title={names}
          >
            {names}
          </Typography>
        );
      }
    }
  ];

  const goldButtonStyle = {
    bgcolor: '#0b7221',
    color: 'white',
    fontWeight: 'bold',
    '&:hover': { bgcolor: '#057727' }
  };

  return (
    <Box sx={{ backgroundColor: '#e8f7e0', minHeight: '100vh', p: 3 }}>
      <Box display="flex" justifyContent="space-between" gap={2} flexDirection={{ xs: 'column', sm: 'row' }} mb={2}>
        <Typography variant="h4" sx={{ color: '#1B5E20', fontWeight: 800,}}>Scheme Management</Typography>

        <Button
          fullWidth={{ xs: true, sm: false }}
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditMode(false);
            setOpen(true);
          }}
          sx={goldButtonStyle}
        >
          Add Scheme
        </Button>
      </Box>

      <Paper>
        {initialLoading ? (
          <Box textAlign="center" p={5}><CircularProgress /></Box>
        ) : (
          <DynamicTable
            data={tableData}
            columns={columns}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />
        )}
      </Paper>

      {/* OFFCANVAS FORM */}
      <DynamicOffcanvas
        open={open}
        onClose={() => setOpen(false)}
        title={editMode ? "Edit Scheme" : "Add Scheme"}
        width={450} 
      >
        <Box mt={2} sx={{ width: '100%' }}>
          <Grid container spacing={2} direction="column">
            
            <Grid item xs={12}>
              <DynamicField
                field={{ name: 'name', label: 'Scheme Name', required: true }}
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12}>
              <DynamicField
                field={{ 
                  name: 'percentage', 
                  label: 'Percentage (%)', 
                  required: true, 
                  type: 'number',
                  // ✅ Added inputProps for HTML5 validation
                  inputProps: { 
                    min: 0, 
                    max: 100,
                    step: 1
                  } 
                }}
                value={formData.percentage}
                onChange={handleChange}
                error={!!errors.percentage}
                helperText={errors.percentage || "Enter a value between 0 and 100"}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" mb={1} fontWeight={600} sx={{ color: '#000' }}>
                Preferred Species
              </Typography>

              <Autocomplete
                multiple
                id="checkboxes-tags-demo"
                options={speciesOptions}
                disableCloseOnSelect
             
                getOptionLabel={(option) => option.species_name || option.name}
                value={formData.species_preferred}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, species_preferred: newValue }));
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <MuiCheckbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.species_name || option.name}
                  </li>
                )}
                style={{ width: '100%' }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Select Species" 
                    placeholder="Search species..." 
                    variant="outlined"
                    size="small"
                    sx={{ bgcolor: '#fff' }}
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.species_name || option.name}
                      {...getTagProps({ index })}
                      sx={{ bgcolor: '#ffc022', color: 'black', fontWeight: 'bold', border: 'none' }}
                    />
                  ))
                }
                ChipProps={{ deleteIcon: <DeleteIcon fontSize="small" /> }}
              />
              
              <FormControlLabel
                sx={{ mt: 1, ml: 1 }}
                label={
                  <Typography variant="body2" fontWeight="bold" color="textSecondary">
                    Select All ({speciesOptions.length})
                  </Typography>
                }
                control={
                  <MuiCheckbox
                    checked={formData.species_preferred.length === speciesOptions.length && speciesOptions.length > 0}
                    indeterminate={formData.species_preferred.length > 0 && formData.species_preferred.length < speciesOptions.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({ ...prev, species_preferred: [...speciesOptions] }));
                      } else {
                        setFormData(prev => ({ ...prev, species_preferred: [] }));
                      }
                    }}
                    sx={{ color: '#ffc022', '&.Mui-checked': { color: '#ffc022' } }}
                  />
                }
              />
            </Grid>

          </Grid>

          <Button
            onClick={handleSubmit}
            startIcon={<Save />}
            fullWidth
            variant="contained"
            sx={{ ...goldButtonStyle, mt: 4, width: '100%' }}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save Scheme"}
          </Button>
        </Box>
      </DynamicOffcanvas>

      {/* DELETE CONFIRMATION MODAL */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchemeManagement;